/* eslint-disable no-unused-vars */
import React, { useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "../AuthContext";
import { HiMicrophone, HiPlay, HiX } from "react-icons/hi";
import { RiMic2Line, RiDeleteBinLine } from "react-icons/ri";
import { 
  FiSearch, FiStar, FiHeart, FiDownload, FiShare2, FiFolder, 
  FiClock, FiBell, FiChevronDown, FiCheck, 
  FiRotateCcw, FiRotateCw, FiClipboard, FiTrash2, FiUpload, FiCopy, FiVolume2, FiMaximize2,
  FiPlay, FiPause, FiSkipBack, FiRepeat, FiVolumeX, FiVolume1, FiVolume2 as FiVolumeHigh
} from "react-icons/fi";

import { VOICE_DATABASE } from "../voices";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";
const API = `${API_BASE}/api`;
const MAX = 5000;

const ALL_V = VOICE_DATABASE;
const LANGS=[["en","🇺🇸 English"],["es","🇪🇸 Spanish"],["fr","🇫🇷 French"],["de","🇩🇪 German"],["it","🇮🇹 Italian"],["pt","🇧🇷 Portuguese"],["pl","🇵🇱 Polish"],["tr","🇹🇷 Turkish"],["ru","🇷🇺 Russian"],["nl","🇳🇱 Dutch"],["cs","🇨🇿 Czech"],["ar","🇸🇦 Arabic"],["zh-cn","🇨🇳 Chinese"],["ja","🇮🇵 Japanese"],["ko","🇰🇷 Korean"],["hi","🇮🇳 Hindi"]];
const TMPLS=[{e:"📢",l:"Announcement",t:"Attention everyone! We have an exciting announcement to share with you today. Please stay tuned for more details."},{e:"🎙",l:"Podcast",t:"Welcome back to another episode. Today we are diving deep into a fascinating topic. Let us get started."},{e:"📱",l:"App",t:"Your request has been processed successfully. Thank you for using our service. Have a wonderful day!"},{e:"📚",l:"E-Learning",t:"In this lesson, we will explore fundamental concepts and how they apply to real-world scenarios step by step."}];

function SettingsModal({ onClose }) {
  const [tab, setTab] = useState("general");
  const [format, setFormat] = useState("WAV");
  const [autosave, setAutosave] = useState(true);

  return (
    <div className="settings-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="settings-modal">
        <div className="settings-sidebar">
          <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.05em",color:"var(--t3)",padding:"0 12px 10px"}}>SETTINGS</div>
          <button className={`settings-tab-btn${tab==="general"?" active":""}`} onClick={()=>setTab("general")}>General</button>
          <button className={`settings-tab-btn${tab==="audio"?" active":""}`} onClick={()=>setTab("audio")}>Audio Output</button>
          <button className={`settings-tab-btn${tab==="studio"?" active":""}`} onClick={()=>setTab("studio")}>Studio Config</button>
          <button className={`settings-tab-btn${tab==="account"?" active":""}`} onClick={()=>setTab("account")}>Account</button>
          <button className="settings-tab-btn" onClick={onClose} style={{marginTop:"auto",color:"var(--rose)"}}>Close Settings</button>
        </div>
        <div className="settings-content">
          {tab==="general" && (
            <>
              <div className="settings-sec-ttl">General Preferences</div>
              <div className="parameter-slider-group">
                <label className="custom-checkbox-row" onClick={()=>setAutosave(!autosave)}>
                  <div className={`custom-checkbox-box ${autosave?"checked":""}`}><FiCheck size={12}/></div>
                  <span>Enable Auto Save generation history state</span>
                </label>
              </div>
              <div className="parameter-slider-group" style={{marginTop:16}}>
                <label className="custom-checkbox-row">
                  <div className="custom-checkbox-box checked"><FiCheck size={12}/></div>
                  <span>Enable Telemetry &amp; Debug Reporting (100% Secure)</span>
                </label>
              </div>
            </>
          )}
          {tab==="audio" && (
            <>
              <div className="settings-sec-ttl">Audio Settings</div>
              <div className="slider-label-row">Output File Format</div>
              <div style={{display:"flex",gap:8,marginTop:8}}>
                {["WAV","MP3","OGG"].map(f=>(
                  <button key={f} className={`settings-tab-btn${format===f?" active":""}`} onClick={()=>setFormat(f)} style={{width:"auto", padding:"8px 16px"}}>{f}</button>
                ))}
              </div>
            </>
          )}
          {tab==="studio" && (
            <>
              <div className="settings-sec-ttl">Studio Configuration</div>
              <div className="slider-label-row">Editor Workspace Font Size</div>
              <div style={{display:"flex",gap:8,marginTop:8}}>
                {["12px","14px","16px","18px"].map(sz=>(
                  <button key={sz} className={`settings-tab-btn${sz==="16px"?" active":""}`} style={{width:"auto", padding:"8px 16px"}}>{sz}</button>
                ))}
              </div>
            </>
          )}
          {tab==="account" && (
            <>
              <div className="settings-sec-ttl">Account Settings</div>
              <div style={{fontSize:13,color:"var(--t2)"}}>Signed in as guest developer. Running on VoiceForge Cloud AI engine.</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Studio({ onNavigate, preset, setPreset }) {
  const { user, logout } = useAuth();
  const [text,    setText]    = useState("");
  const [file,    setFile]    = useState(null);
  const [mode,    setMode]    = useState("preset");
  const [lang,    setLang]    = useState("en");
  const [detectedLang, setDetectedLang] = useState("en");

  const getFlag = (code) => {
    switch (code) {
      case "ur": return "Urdu 🇵🇰";
      case "en": return "English 🇺🇸";
      case "hi": return "Hindi 🇮🇳";
      case "ar": return "Arabic 🇸🇦";
      case "zh-cn": return "Chinese 🇨🇳";
      case "fr": return "French 🇫🇷";
      case "de": return "German 🇩🇪";
      case "es": return "Spanish 🇪🇸";
      case "ja": return "Japanese 🇯🇵";
      case "ko": return "Korean 🇰🇷";
      default: return code.toUpperCase();
    }
  };

  const autoSwitchVoice = () => {
    const compatible = ALL_V.find(v => v.langCode === detectedLang);
    if (compatible) {
      setPreset(compatible.value);
      triggerToast(`Switched to compatible voice: ${compatible.name}`);
    }
  };

  useEffect(() => {
    if (!text.trim()) return;
    let code = "en";
    if (/[\u0600-\u06FF]/.test(text)) code = "ur";
    else if (/[\u0900-\u097F]/.test(text)) code = "hi";
    else if (/[\u4E00-\u9FFF]/.test(text)) code = "zh-cn";
    else if (/[\u3040-\u30FF]/.test(text)) code = "ja";
    else if (/[\uAC00-\uD7AF]/.test(text)) code = "ko";
    else if (/[\u0400-\u04FF]/.test(text)) code = "ru";
    else if (/[äöüßéèàùçáéíóúñ]/i.test(text)) {
      const curV = ALL_V.find(v => v.value === preset) || ALL_V[0];
      const curL = curV.langCode || "en";
      if (["es", "de", "fr"].includes(curL)) {
        code = curL;
      } else {
        code = "es";
      }
    }
    setDetectedLang(code);
    setLang(code);
  }, [text, preset]);

  const [audioUrl,setAudioUrl]= useState(null);
  const [loading, setLoading] = useState(false);
  const [,   setError]   = useState(null);
  const [status,  setStatus]  = useState("");
  const [elapsed, setElapsed] = useState(null);
  const [history, setHistory] = useState([]);
  const [ferr,    setFerr]    = useState(null);
  const [speed,   setSpeed]   = useState(1.0);
  const [pitch,   setPitch]   = useState(0);
  const [copied,  setCopied]  = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Undo/Redo States
  const [textHistory, setTextHistory] = useState([""]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Additional Premium Parameters
  const [stability, setStability]     = useState(75);
  const [similarity, setSimilarity]   = useState(85);
  const [emotion, setEmotion]         = useState("Natural");
  const [accent, setAccent]           = useState("Neutral Accent");
  const [pauseControl, setPauseControl] = useState(50);
  const [energy, setEnergy]           = useState(60);
  const [clarity, setClarity]         = useState(80);
  const [warmth, setWarmth]           = useState(70);
  const [boost, setBoost]             = useState(true);

  // Dropdown states
  const [langOpen, setLangOpen] = useState(false);
  const [accentOpen, setAccentOpen] = useState(false);
  const [emotionOpen, setEmotionOpen] = useState(false);

  // Premium Voice Browser Modal States
  const [libOpen, setLibOpen] = useState(false);
  const [libSearch, setLibSearch] = useState("");
  
  // Advanced Filter Pills
  const [filterGender, setFilterGender] = useState("all");
  const [filterLang, setFilterLang] = useState("all");
  const [filterStyle, setFilterStyle] = useState("all");
  const [filterPro, setFilterPro] = useState("all");
  const [filterTrending, setFilterTrending] = useState(false);
  const [filterNew, setFilterNew] = useState(false);

  // Favorites & Recents
  const [favorites, setFavorites] = useState({});
  const [recentVoices, setRecentVoices] = useState([]);

  // Preview Playback State (HTML5 SpeechSynthesis wrapper)
  const [previewingVoice, setPreviewingVoice] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(0);
  const [previewTime, setPreviewTime] = useState(0);
  const [isPlaybackLoading, setIsPlaybackLoading] = useState(false);
  
  // Side Details Panel State
  const [detailVoice, setDetailVoice] = useState(null);

  // Toast Notification State
  const [toastMessage, setToastMessage] = useState(null);
  const [toastActive, setToastActive] = useState(false);

  // Custom Audio Player States
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioVolume, setAudioVolume] = useState(0.8);
  const [audioMuted, setAudioMuted] = useState(false);
  const [audioPlaybackRate, setAudioPlaybackRate] = useState(1.0);
  const [audioLoop, setAudioLoop] = useState(false);
  const [genMetadata, setGenMetadata] = useState(null);

  const fref = useRef(null);
  const t0   = useRef(null);
  const audioContextRef = useRef(null);
  const previewTimerRef = useRef(null);
  const audioRef = useRef(null);
  const previewAudioRef = useRef(null);

  useEffect(()=>()=>{
    if(audioUrl)URL.revokeObjectURL(audioUrl);
    if(audioContextRef.current) audioContextRef.current.close();
    if(previewTimerRef.current) clearInterval(previewTimerRef.current);
    if(window.speechSynthesis) window.speechSynthesis.cancel();
    if(previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
  },[audioUrl]);

  const [savedClones, setSavedClones] = useState([]);
  const [selectedCloneId, setSelectedCloneId] = useState(null);
  const [cloneNameInput, setCloneNameInput] = useState("");
  const [isSavingClone, setIsSavingClone] = useState(false);
  const [playingCloneId, setPlayingCloneId] = useState(null);
  const [cloneAudioElement, setCloneAudioElement] = useState(null);

  const fetchClones = async () => {
    try {
      const res = await fetch(`${API}/clones`);
      if (res.ok) {
        const data = await res.json();
        setSavedClones(data);
      }
    } catch (e) {
      console.error("Failed to fetch cloned voices:", e);
    }
  };

  useEffect(() => {
    fetchClones();
  }, []);

  const saveClone = async () => {
    if (!file) return triggerToast("Please upload or drag a voice sample file first.");
    if (!cloneNameInput.trim()) return triggerToast("Please enter a name for your cloned voice.");
    setIsSavingClone(true);
    try {
      const fd = new FormData();
      fd.append("name", cloneNameInput);
      fd.append("voiceFile", file);
      fd.append("duration", file.duration || 0);

      const res = await fetch(`${API}/clones`, {
        method: "POST",
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save cloned voice.");
      
      triggerToast("Voice cloned and saved successfully!");
      setCloneNameInput("");
      setFile(null);
      fetchClones();
    } catch (e) {
      triggerToast(e.message);
    } finally {
      setIsSavingClone(false);
    }
  };

  const deleteClone = async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this cloned voice?")) return;
    try {
      const res = await fetch(`${API}/clones/${id}`, { method: "DELETE" });
      if (res.ok) {
        triggerToast("Cloned voice deleted successfully.");
        if (selectedCloneId === id) setSelectedCloneId(null);
        fetchClones();
      } else {
        const data = await res.json();
        triggerToast(data.error || "Failed to delete cloned voice.");
      }
    } catch (e) {
      triggerToast(e.message);
    }
  };

  const renameClone = async (id, currentName) => {
    const newName = prompt("Enter new name for the cloned voice:", currentName);
    if (!newName || !newName.trim()) return;
    try {
      const res = await fetch(`${API}/clones/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName.trim() })
      });
      if (res.ok) {
        triggerToast("Cloned voice renamed successfully.");
        fetchClones();
      } else {
        const data = await res.json();
        triggerToast(data.error || "Failed to rename cloned voice.");
      }
    } catch (e) {
      triggerToast(e.message);
    }
  };

  const togglePlayCloneSample = (clone, e) => {
    if (e) e.stopPropagation();
    if (playingCloneId === clone.id) {
      if (cloneAudioElement) {
        cloneAudioElement.pause();
      }
      setPlayingCloneId(null);
      setCloneAudioElement(null);
      return;
    }

    if (cloneAudioElement) {
      cloneAudioElement.pause();
    }

    const filename = clone.path.split("/").pop();
    const audio = new Audio(`${API}/audio/${filename}`);
    audio.play();
    setPlayingCloneId(clone.id);
    setCloneAudioElement(audio);
    audio.onended = () => {
      setPlayingCloneId(null);
      setCloneAudioElement(null);
    };
  };

  // Toast helper
  const triggerToast = (msg) => {
    setToastMessage(msg);
    setToastActive(true);
    setTimeout(() => setToastActive(false), 3000);
  };

  // Speaks using window.speechSynthesis fallback or fetches real preset preview WAV
  const playSynthPreview = (voiceValue) => {
    if (previewingVoice === voiceValue) {
      stopSynthPreview();
      return;
    }

    stopSynthPreview();
    setIsPlaybackLoading(true);
    setPreviewingVoice(voiceValue);
    setPreviewProgress(0);
    setPreviewTime(0);

    const voiceObj = ALL_V.find(v => v.value === voiceValue);
    if (!voiceObj) return;

    // Use our new backend preview endpoint to fetch real Piper synthesized audio for the voice!
    const sentence = voiceObj.sentence || "This is a voice preview.";
    const langCode = voiceObj.langCode || "en";
    const audioUrl = `${API}/preview/${voiceValue}?text=${encodeURIComponent(sentence)}&lang=${langCode}`;
    
    const audio = new Audio(audioUrl);
    previewAudioRef.current = audio;

    audio.onplay = () => {
      setIsPlaybackLoading(false);
    };

    audio.ontimeupdate = () => {
      if (audio.duration) {
        setPreviewTime(audio.currentTime);
        setPreviewProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    audio.onended = () => {
      stopSynthPreview();
    };

    audio.onerror = (e) => {
      console.error("Preview audio playback error", e);
      stopSynthPreview();
    };

    audio.play().catch(err => {
      console.error("Failed to play preview audio", err);
      // Fallback to local browser TTS if server preview fails
      setIsPlaybackLoading(false);
      if (window.speechSynthesis) {
        const utterance = new SpeechSynthesisUtterance(sentence);
        window.speechSynthesis.speak(utterance);
      }
    });
  };

  const stopSynthPreview = () => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setPreviewingVoice(null);
    setPreviewProgress(0);
    setPreviewTime(0);
    setIsPlaybackLoading(false);
  };

  // Selection Handler
  const selectVoice = (value) => {
    setPreset(value);
    setLibOpen(false);
    stopSynthPreview();
    
    // Add to recents
    setRecentVoices(prev => {
      const filtered = prev.filter(v => v !== value);
      return [value, ...filtered].slice(0, 5);
    });

    triggerToast("Voice selected successfully.");
  };

  // Toggle Favorites
  const toggleFavorite = (val, e) => {
    e.stopPropagation();
    setFavorites(prev => ({ ...prev, [val]: !prev[val] }));
    const isFavNow = !favorites[val];
    triggerToast(isFavNow ? `❤️ Added to Favorites` : `💔 Removed from Favorites`);
  };

  // Handle text history change for Undo/Redo
  const handleTextChange = (newVal) => {
    if (newVal.length <= MAX) {
      setText(newVal);
      const nextHist = textHistory.slice(0, historyIndex + 1);
      setTextHistory([...nextHist, newVal]);
      setHistoryIndex(nextHist.length);
    }
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setText(textHistory[historyIndex - 1]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < textHistory.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setText(textHistory[historyIndex + 1]);
    }
  };

  const importText = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".txt";
    input.onchange = (e) => {
      const f = e.target.files[0];
      if (f) {
        const reader = new FileReader();
        reader.onload = (evt) => handleTextChange(evt.target.result);
        reader.readAsText(f);
      }
    };
    input.click();
  };

  const exportText = () => {
    if (!text.trim()) return;
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "voiceforge_script.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const detectLang=()=>{
    if(!text.trim())return;
    if(/[\u0600-\u06FF]/.test(text))return setLang("ur");
    if(/[\u4E00-\u9FFF]/.test(text))return setLang("zh-cn");
    if(/[\u3040-\u30FF]/.test(text))return setLang("ja");
    if(/[\uAC00-\uD7AF]/.test(text))return setLang("ko");
    if(/[\u0900-\u097F]/.test(text))return setLang("hi");
    if(/[\u0400-\u04FF]/.test(text))return setLang("ru");
    setLang("en");
  };

  const validateFile=f=>{
    setFerr(null);
    if(!f){setFile(null);return;}
    if(f.size>50*1024*1024){setFerr("File too large. Max 50 MB.");return;}
    const ext=f.name.split(".").pop().toLowerCase();
    if(!["wav","mp3","ogg","flac"].includes(ext)){setFerr("Use WAV, MP3, OGG or FLAC.");return;}
    setFile(f);
  };

  const generate=useCallback(async()=>{
    if(!text.trim()){setError("Please enter some text.");return;}
    if(mode==="clone"&&!file&&!selectedCloneId){setError("Upload a voice sample or select a saved clone first.");return;}
    setError(null);setLoading(true);setStatus("Uploading...");setElapsed(null);
    t0.current=Date.now();
    if(audioUrl){URL.revokeObjectURL(audioUrl);setAudioUrl(null);}
    try{
      const fd = new FormData();
      fd.append("text", text);
      fd.append("speed", speed);
      fd.append("pitch", pitch);
      fd.append("language", lang);
      fd.append("voiceMode", mode);
      if(mode==="clone") {
        if (selectedCloneId) {
          fd.append("clonedVoiceId", selectedCloneId);
        } else if (file) {
          fd.append("voiceFile", file);
        }
      } else {
        fd.append("presetVoiceName", preset);
      }
      
      const res = await fetch(`${API}/generate`, { method: "POST", body: fd });
      if(!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Backend server error.");
      }
      const data = await res.json();
      if(!data.jobId) throw new Error("Failed to start speech job.");
      
      const jobId=data.jobId;const deadline=Date.now()+180000;
      while(Date.now()<deadline){
        const pr=await fetch(`${API}/generate/${jobId}`);const pd=await pr.json();
        if(pd.status==="failed"){
          throw new Error(pd.error || "Speech synthesis job failed.");
        }
        if(pd.progress?.stage==="loading_model")setStatus("Loading model…");
        else if(pd.progress?.stage==="preprocessing")setStatus("Processing Audio...");
        else if(pd.progress?.stage==="extracting_features")setStatus("Extracting Speaker Features...");
        else if(pd.progress?.stage==="creating_profile")setStatus("Creating Voice Profile...");
        else if(pd.progress?.stage==="generating")setStatus("Generating Speech...");
        else if(pd.status==="running")setStatus("Synthesizing…");
        
        if(pd.status==="succeeded"&&pd.result?.outputFilename){
          setStatus("Completed ✅");
          const url=`${API}/audio/${pd.result.outputFilename}?t=${Date.now()}`;
          setAudioUrl(url);setError(null);
          const el=((Date.now()-t0.current)/1000).toFixed(1);setElapsed(el);
          const v=ALL_V.find(v=>v.value===preset);
          
          setGenMetadata({
            voiceName: mode === "clone" ? (selectedCloneId ? (savedClones.find(c=>c.id === selectedCloneId)?.name || "Cloned Voice") : "Cloned Voice") : (v?.name || preset),
            language: LANGS.find(([code]) => code === lang)?.[1] || lang,
            duration: el,
            characters: text.length,
            time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            fileSize: pd.result?.sizeBytes ? `${(pd.result.sizeBytes / 1024).toFixed(1)} KB` : "Unknown size",
            format: "WAV",
            sampleRate: "44.1 kHz"
          });

          setHistory(h=>[{url,el,name:mode==="clone"?(selectedCloneId ? (savedClones.find(c=>c.id === selectedCloneId)?.name || "Cloned") : "Cloned"):(v?.name||preset),time:new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),preview:text.trim().slice(0,50)+(text.length>50?"…":"")}, ...h].slice(0,10));
          
          if (mode === "preset") {
            setRecentVoices(prev => {
              const filtered = prev.filter(val => val !== preset);
              return [preset, ...filtered].slice(0, 5);
            });
          }

          setStatus("Done ✓");
          
          // Auto scroll to results section
          setTimeout(() => {
            document.getElementById("generated-audio-section")?.scrollIntoView({ behavior: "smooth" });
          }, 150);

          break;
        }
        if(pd.status==="failed")throw new Error(pd.error?.message||"Generation failed.");
        await new Promise(r=>setTimeout(r,900));
      }
    }catch(e){setError(e.message?.includes("fetch")?"Cannot connect to backend on port 5000.":e.message||"Generation failed.");setStatus("");}
    finally{setLoading(false);}
  },[text,file,mode,preset,lang,audioUrl,speed,pitch,selectedCloneId,savedClones]);

  const onKey=e=>{if(e.key==="Escape"&&(libOpen||settingsOpen))return;if(e.ctrlKey&&e.key==="Enter"&&!loading&&text.trim()){e.preventDefault();generate();}};
  const disabled=!text.trim()||loading||(mode==="clone"&&!file&&!selectedCloneId);
  const wc=text.trim()?text.trim().split(/\s+/).length:0;
  const estSec=wc>0?Math.round((wc/130)*60):0;
  const curV=ALL_V.find(v=>v.value===preset)||ALL_V[0];
  
  const copyText=()=>{if(!text.trim())return;navigator.clipboard.writeText(text).then(()=>{setCopied(true);setTimeout(()=>setCopied(false),2000);});};
  const clearText=()=>{setText("");};
  const pasteText=async()=>{try{const val=await navigator.clipboard.readText();handleTextChange(val);}catch(e){}};

  const [saveSuccess, setSaveSuccess] = useState(false);
  const saveToHistory = () => {
    if (!audioUrl) return;
    const v = ALL_V.find(x => x.value === preset);
    const item = {
      url: audioUrl,
      el: elapsed || "0.0",
      name: mode === "clone" ? "Cloned" : (v?.name || preset),
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      preview: text.trim().slice(0, 50) + (text.length > 50 ? "…" : "")
    };
    setHistory(h => {
      if (h.some(x => x.url === audioUrl)) return h;
      return [item, ...h].slice(0, 10);
    });
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  };

  const deleteHistoryItem = (e, url) => {
    e.stopPropagation();
    setHistory(prev => prev.filter(x => x.url !== url));
  };

  const currentLangLabel = (LANGS.find(([code]) => code === lang) || [lang, lang])[1];

  // Helper to retrieve voices filtered by current selection and query
  const getCategorizedVoices = (categoryKey) => {
    return ALL_V.filter(v => {
      const matchesSearch = v.name.toLowerCase().includes(libSearch.toLowerCase()) || 
                            v.desc.toLowerCase().includes(libSearch.toLowerCase());
      if (!matchesSearch) return false;

      if (filterGender !== "all" && v.gender !== filterGender) return false;
      if (filterLang !== "all" && v.lang !== filterLang) return false;
      if (filterStyle !== "all" && v.style !== filterStyle) return false;
      if (filterPro === "pro" && !v.pro) return false;
      if (filterPro === "free" && v.pro) return false;
      if (filterTrending && !v.trending) return false;
      if (filterNew && !v.isNew) return false;

      if (categoryKey === "recommended") return v.recommended;
      if (categoryKey === "trending") return v.trending;
      if (categoryKey === "favorites") return !!favorites[v.value];
      if (categoryKey === "recent") return recentVoices.includes(v.value);
      
      // Regional Category Matches
      if (categoryKey === "english") return v.langCode === "en" && v.accent.toLowerCase().includes("american");
      if (categoryKey === "british") return v.langCode === "en" && v.accent.toLowerCase().includes("british");
      if (categoryKey === "hindi") return v.langCode === "hi";
      if (categoryKey === "urdu") return v.langCode === "ur";
      if (categoryKey === "arabic") return v.langCode === "ar";
      if (categoryKey === "french") return v.langCode === "fr";
      if (categoryKey === "german") return v.langCode === "de";
      if (categoryKey === "spanish") return v.langCode === "es";
      if (categoryKey === "japanese") return v.langCode === "ja";
      if (categoryKey === "korean") return v.langCode === "ko";
      if (categoryKey === "chinese") return v.langCode === "zh-cn";

      if (categoryKey === "male") return v.gender === "male";
      if (categoryKey === "female") return v.gender === "female";
      if (categoryKey === "narration") return v.style === "Narration";
      if (categoryKey === "audiobook") return v.style === "Audiobook";
      if (categoryKey === "gaming") return v.style === "Gaming";
      if (categoryKey === "cinematic") return v.style === "Cinematic";
      if (categoryKey === "assistant") return v.style === "AI Assistant";
      if (categoryKey === "support") return v.style === "Customer Support";

      return true;
    });
  };

  // Custom audio player events
  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      setAudioCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleAudioLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  const handleAudioEnded = () => {
    setAudioPlaying(false);
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (audioPlaying) {
        audioRef.current.pause();
        setAudioPlaying(false);
      } else {
        audioRef.current.play();
        setAudioPlaying(true);
      }
    }
  };

  const restartAudio = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      setAudioPlaying(true);
    }
  };

  const skipAudio = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(audioDuration, audioRef.current.currentTime + seconds));
    }
  };

  const seekAudio = (val) => {
    if (audioRef.current) {
      audioRef.current.currentTime = val;
      setAudioCurrentTime(val);
    }
  };

  const changeVolume = (val) => {
    if (audioRef.current) {
      audioRef.current.volume = val;
      setAudioVolume(val);
      setAudioMuted(val === 0);
      audioRef.current.muted = val === 0;
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      const nextMute = !audioMuted;
      audioRef.current.muted = nextMute;
      setAudioMuted(nextMute);
    }
  };

  const changePlaybackRate = (rate) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
      setAudioPlaybackRate(rate);
    }
  };

  const toggleLoop = () => {
    if (audioRef.current) {
      const nextLoop = !audioLoop;
      audioRef.current.loop = nextLoop;
      setAudioLoop(nextLoop);
    }
  };

  // Formats time from seconds into MM:SS format
  const formatTime = (timeInSecs) => {
    if (isNaN(timeInSecs)) return "00:00";
    const mins = Math.floor(timeInSecs / 60);
    const secs = Math.floor(timeInSecs % 60);
    return `${mins < 10 ? "0" : ""}${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <div className="page-wrap" onKeyDown={onKey} tabIndex={-1} style={{background:"#050505", minHeight:"100vh", display:"flex", flexDirection:"column"}}>
      {settingsOpen&&<SettingsModal onClose={()=>setSettingsOpen(false)}/>}

      {/* Floating success toast */}
      {toastActive && (
        <div className="toast-notification">
          <span>{toastMessage}</span>
        </div>
      )}

      {/* ── RESTORED VOICE BROWSER MODAL ── */}
      {libOpen && (
        <div className="voice-browser-overlay" onClick={e=>e.target===e.currentTarget&&setLibOpen(false)}>
          <div className="voice-browser-modal">
            <div className="voice-browser-header">
              <div>
                <h1 style={{fontSize:22, fontWeight:800, color:"#fff"}}>Voice Library</h1>
                <p style={{fontSize:13.5, color:"#B4B4C5", marginTop:4}}>Browse and select standard & premium AI voice models for your scripts</p>
              </div>
              <button className="vmd-x" onClick={()=>{setLibOpen(false); stopSynthPreview();}}><HiX size={14}/></button>
            </div>

            {/* Main Area: Scrollable rows and details sidebar */}
            <div className={`voice-browser-content${detailVoice ? " with-details" : ""}`}>
              
              <div className="voice-browser-scrollarea">
                {/* Search voices and pills */}
                <div style={{display:"flex", flexDirection:"column", gap:20, borderBottom:"1px solid var(--border)", paddingBottom:24}}>
                  <div style={{position:"relative", width:"100%"}}>
                    <FiSearch size={16} style={{position:"absolute", left:16, top:"50%", transform:"translateY(-50%)", color:"#808090", pointerEvents:"none"}}/>
                    <input type="text" placeholder="Search by name, description, gender, use case..." value={libSearch} onChange={e=>setLibSearch(e.target.value)} style={{width:"100%", height:48, background:"#111118", border:"1px solid var(--border)", borderRadius:14, padding:"0 20px 0 44px", color:"#fff", fontSize:14.5, outline:"none"}}/>
                  </div>

                  {/* Filter Row */}
                  <div style={{display:"flex", gap:10, flexWrap:"wrap", alignItems:"center"}}>
                    
                    {/* Gender Filter Button */}
                    <div style={{position:"relative"}}>
                      <select value={filterGender} onChange={e=>setFilterGender(e.target.value)} style={{background:"#111118", border:"1px solid var(--border)", color:"#B4B4C5", padding:"8px 16px", borderRadius:12, fontSize:13, outline:"none", cursor:"pointer"}}>
                        <option value="all">Any Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>

                    {/* Language Filter Button */}
                    <div style={{position:"relative"}}>
                      <select value={filterLang} onChange={e=>setFilterLang(e.target.value)} style={{background:"#111118", border:"1px solid var(--border)", color:"#B4B4C5", padding:"8px 16px", borderRadius:12, fontSize:13, outline:"none", cursor:"pointer"}}>
                        <option value="all">Any Language</option>
                        <option value="🇺🇸 English">🇺🇸 English</option>
                        <option value="🇬🇧 English">🇬🇧 English</option>
                      </select>
                    </div>

                    {/* Style Filter */}
                    <div style={{position:"relative"}}>
                      <select value={filterStyle} onChange={e=>setFilterStyle(e.target.value)} style={{background:"#111118", border:"1px solid var(--border)", color:"#B4B4C5", padding:"8px 16px", borderRadius:12, fontSize:13, outline:"none", cursor:"pointer"}}>
                        <option value="all">Any Style</option>
                        <option value="Narration">Narration</option>
                        <option value="Audiobook">Audiobook</option>
                        <option value="Gaming">Gaming</option>
                        <option value="Cinematic">Cinematic</option>
                        <option value="AI Assistant">AI Assistant</option>
                        <option value="Customer Support">Customer Support</option>
                      </select>
                    </div>

                    {/* Tier Filter */}
                    <button onClick={()=>setFilterPro(prev => prev==="pro"?"all":"pro")} style={{
                      padding:"8px 16px", borderRadius:12, border:"1px solid var(--border)", fontSize:13, cursor:"pointer",
                      background:filterPro==="pro"?"rgba(139,92,246,0.15)":"#111118",
                      color:filterPro==="pro"?"#fff":"#B4B4C5", borderColor:filterPro==="pro"?"rgba(139,92,246,0.3)":"var(--border)"
                    }}>
                      PRO Voices
                    </button>

                    {/* Trending Pill */}
                    <button onClick={()=>setFilterTrending(p => !p)} style={{
                      padding:"8px 16px", borderRadius:12, border:"1px solid var(--border)", fontSize:13, cursor:"pointer",
                      background:filterTrending?"rgba(139,92,246,0.15)":"#111118",
                      color:filterTrending?"#fff":"#B4B4C5", borderColor:filterTrending?"rgba(139,92,246,0.3)":"var(--border)"
                    }}>
                      🔥 Trending
                    </button>

                    {/* New Voices Pill */}
                    <button onClick={()=>setFilterNew(p => !p)} style={{
                      padding:"8px 16px", borderRadius:12, border:"1px solid var(--border)", fontSize:13, cursor:"pointer",
                      background:filterNew?"rgba(139,92,246,0.15)":"#111118",
                      color:filterNew?"#fff":"#B4B4C5", borderColor:filterNew?"rgba(139,92,246,0.3)":"var(--border)"
                    }}>
                      ✨ New Voices
                    </button>

                  </div>
                </div>

                {/* Categories with Automatic Filtering/Sorting */}
                {(() => {
                  const baseCats = [
                    {id:"recommended", label:"Recommended", icon:"⭐"},
                    {id:"english", label:"English Voices", icon:"🇺🇸"},
                    {id:"british", label:"British Voices", icon:"🇬🇧"},
                    {id:"hindi", label:"Hindi Voices", icon:"🇮🇳"},
                    {id:"urdu", label:"Urdu Voices", icon:"🇵🇰"},
                    {id:"arabic", label:"Arabic Voices", icon:"🇸🇦"},
                    {id:"french", label:"French Voices", icon:"🇫🇷"},
                    {id:"german", label:"German Voices", icon:"🇩🇪"},
                    {id:"spanish", label:"Spanish Voices", icon:"🇪🇸"},
                    {id:"japanese", label:"Japanese Voices", icon:"🇯🇵"},
                    {id:"korean", label:"Korean Voices", icon:"🇰🇷"},
                    {id:"chinese", label:"Chinese Voices", icon:"🇨🇳"},
                    {id:"favorites", label:"Favorites", icon:"⭐"},
                    {id:"recent", label:"Recently Used", icon:"🕘"}
                  ];
                  const activeLang = lang || detectedLang || "en";
                  if (activeLang === "ur") {
                    const idx = baseCats.findIndex(c => c.id === "urdu");
                    if (idx !== -1) {
                      const [urduCat] = baseCats.splice(idx, 1);
                      baseCats.splice(1, 0, urduCat); // Place Urdu Voices right after Recommended
                    }
                  } else if (activeLang === "en") {
                    const idx = baseCats.findIndex(c => c.id === "english");
                    if (idx !== -1) {
                      const [engCat] = baseCats.splice(idx, 1);
                      baseCats.splice(1, 0, engCat); // Place English Voices right after Recommended
                    }
                  } else {
                    const idx = baseCats.findIndex(c => c.id === activeLang);
                    if (idx !== -1) {
                      const [matchedCat] = baseCats.splice(idx, 1);
                      baseCats.splice(1, 0, matchedCat);
                    }
                  }
                  return baseCats;
                })().map(cat => {
                  const voicesInCat = getCategorizedVoices(cat.id);
                  if (voicesInCat.length === 0) return null;
                  
                  return (
                    <div key={cat.id} className="voice-category-block">
                      <div className="voice-category-title">
                        <span>{cat.icon}</span>
                        <span>{cat.label}</span>
                        <span style={{fontSize:12, color:"#808090", fontWeight:500}}>({voicesInCat.length})</span>
                      </div>
                      <div className="voice-category-row">
                        {voicesInCat.map(v => (
                          <div key={v.value} className={`voice-browser-card${preset===v.value?" active":""}`} onClick={()=>setPreset(v.value)}>
                            
                            {/* Card Top Row */}
                            <div style={{display:"flex", alignItems:"center", gap:12}}>
                              <div className={`vshow-av ${v.gender==="female"?"vav-f":"vav-m"}`} style={{
                                width:40, height:40, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center",
                                background:v.gender==="female"?"rgba(244, 63, 94, 0.1)":"rgba(56, 189, 248, 0.1)",
                                color:v.gender==="female"?"var(--rose)":"var(--sky)", fontSize:15, fontWeight:700
                              }}>
                                {v.gender==="female"?"♀":"♂"}
                              </div>
                              <div style={{flex:1, minWidth:0}}>
                                <div style={{display:"flex", alignItems:"center", gap:6}}>
                                  <span style={{fontWeight:750, color:"#fff", fontSize:15}}>{v.name}</span>
                                  {v.pro && <span style={{fontSize:8,fontWeight:700,color:"#fbbf24",background:"rgba(251,191,36,0.1)",padding:"1px 4px",borderRadius:100}}>PRO</span>}
                                  {v.isNew && <span style={{fontSize:8,fontWeight:700,color:"#10b981",background:"rgba(16,185,129,0.1)",padding:"1px 4px",borderRadius:100}}>NEW</span>}
                                </div>
                                <div style={{fontSize:11.5, color:"#B4B4C5", marginTop:2}}>{v.accent}</div>
                              </div>
                            </div>

                            {/* Card Body details */}
                            <div style={{fontSize:12.5, color:"#B4B4C5", lineHeight:1.4}}>{v.desc}</div>

                            {/* Detailed Metadata Badges */}
                            <div style={{display:"flex", flexWrap:"wrap", gap:6, marginTop:8, marginBottom:8}}>
                              {v.langCode === "ur" && (
                                <span style={{fontSize:10, fontWeight:700, color:"#10b981", background:"rgba(16,185,129,0.1)", border:"1px solid rgba(16,185,129,0.15)", padding:"2px 6px", borderRadius:6, display:"inline-flex", alignItems:"center", gap:2}}>
                                  🇵🇰 Urdu
                                </span>
                              )}
                              <span style={{fontSize:10, fontWeight:600, color:"#a855f7", background:"rgba(168,85,247,0.08)", border:"1px solid rgba(168,85,247,0.15)", padding:"2px 6px", borderRadius:6, textTransform:"capitalize"}}>
                                {v.gender}
                              </span>
                              <span style={{fontSize:10, fontWeight:600, color:"#3b82f6", background:"rgba(59,130,246,0.08)", border:"1px solid rgba(59,130,246,0.15)", padding:"2px 6px", borderRadius:6}}>
                                Style: {v.style || "Natural"}
                              </span>
                            </div>

                            {/* Preview playback panel inside card */}
                            {previewingVoice === v.value && (
                              <div style={{background:"rgba(139,92,246,0.06)", border:"1px solid rgba(139,92,246,0.2)", borderRadius:12, padding:"8px 12px", display:"flex", flexDirection:"column", gap:6}}>
                                <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", width:"100%"}}>
                                  <span style={{fontSize:10, fontFamily:"var(--mono)", color:"#C084FC"}}>{previewTime.toFixed(1)}s / 3.5s</span>
                                  {isPlaybackLoading && <span className="gen-spin" style={{width:8, height:8}}/>}
                                </div>
                                <div style={{height:4, background:"rgba(255,255,255,0.05)", borderRadius:4, overflow:"hidden"}}>
                                  <div style={{height:"100%", background:"#8B5CF6", width:`${previewProgress}%`}}/>
                                </div>
                                {/* Waveform animated lines */}
                                <div style={{display:"flex", gap:3, height:12, alignItems:"center", justifyContent:"center"}}>
                                  {[1,2,3,4,5,6,7,8].map(i=>(
                                    <div key={i} className="wave-bar" style={{height:`${Math.random()*10+2}px`, width:2, background:"#8B5CF6", animationDuration:`${Math.random()*0.5+0.5}s`}}/>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Action Buttons bar */}
                            <div style={{display:"flex", gap:6, marginTop:"auto"}} onClick={e=>e.stopPropagation()}>
                              <button onClick={()=>playSynthPreview(v.value)} style={{
                                flex:1, height:32, background:"rgba(255,255,255,0.03)", border:"1px solid var(--border)",
                                borderRadius:10, color:"#fff", fontSize:11.5, fontWeight:600, display:"flex", alignItems:"center", justifyContent:"center", gap:6, cursor:"pointer"
                              }}>
                                {previewingVoice===v.value ? <FiPause size={12}/> : <FiVolume2 size={12}/>}
                                {previewingVoice===v.value ? "Pause" : "Preview"}
                              </button>
                              <button onClick={()=>selectVoice(v.value)} style={{
                                flex:1.2, height:32, background:preset===v.value?"#8B5CF6":"rgba(139,92,246,0.1)",
                                border:"1px solid rgba(139,92,246,0.2)", borderRadius:10, color:"#fff", fontSize:11.5, fontWeight:600, display:"flex", alignItems:"center", justifyContent:"center", gap:4, cursor:"pointer"
                              }}>
                                {preset===v.value ? "Selected" : "Select Voice"}
                              </button>
                              <button onClick={(e)=>toggleFavorite(v.value, e)} style={{
                                width:32, height:32, background:"transparent", border:"1px solid var(--border)",
                                borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer",
                                color:favorites[v.value]?"#fbbf24":"#808090"
                              }}>
                                <FiHeart size={12} fill={favorites[v.value]?"currentColor":"none"}/>
                              </button>
                              <button onClick={()=>setDetailVoice(v)} style={{
                                width:32, height:32, background:"transparent", border:"1px solid var(--border)",
                                borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"#B4B4C5"
                              }} title="Details">
                                <FiMaximize2 size={12}/>
                              </button>
                            </div>

                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Side Details Panel */}
              {detailVoice && (
                <aside className="voice-details-panel">
                  <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                    <div style={{fontSize:11, fontWeight:800, color:"#A855F7", letterSpacing:"0.1em", textTransform:"uppercase"}}>Voice Details</div>
                    <button onClick={()=>setDetailVoice(null)} style={{background:"transparent", border:"none", cursor:"pointer", color:"#808090"}}><HiX size={12}/></button>
                  </div>

                  <div style={{display:"flex", flexDirection:"column", alignItems:"center", gap:14, textAlign:"center"}}>
                    <div className={`vshow-av ${detailVoice.gender==="female"?"vav-f":"vav-m"}`} style={{
                      width:72, height:72, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center",
                      background:detailVoice.gender==="female"?"rgba(244, 63, 94, 0.1)":"rgba(56, 189, 248, 0.1)",
                      color:detailVoice.gender==="female"?"var(--rose)":"var(--sky)", fontSize:28, fontWeight:700
                    }}>
                      {detailVoice.gender==="female"?"♀":"♂"}
                    </div>
                    <div>
                      <h2 style={{fontSize:18, fontWeight:800, color:"#fff"}}>{detailVoice.name}</h2>
                      <p style={{fontSize:13, color:"#B4B4C5", marginTop:4}}>{detailVoice.accent} · {detailVoice.lang}</p>
                    </div>
                  </div>

                  <div style={{display:"flex", flexDirection:"column", gap:16, borderTop:"1px solid var(--border)", paddingTop:20}}>
                    <div>
                      <div style={{fontSize:12, color:"#808090"}}>Description</div>
                      <div style={{fontSize:13.5, color:"#fff", marginTop:4, lineHeight:1.5}}>{detailVoice.desc}</div>
                    </div>
                    <div>
                      <div style={{fontSize:12, color:"#808090"}}>Best For</div>
                      <div style={{fontSize:13.5, color:"#fff", marginTop:4, fontWeight:600}}>{detailVoice.bestFor}</div>
                    </div>
                    <div>
                      <div style={{fontSize:12, color:"#808090"}}>Typical Use Cases</div>
                      <div style={{fontSize:13.5, color:"#fff", marginTop:4}}>{detailVoice.useCases}</div>
                    </div>
                    <div>
                      <div style={{fontSize:12, color:"#808090"}}>Clone Compatibility</div>
                      <div style={{fontSize:13.5, color:"#fff", marginTop:4}}>{detailVoice.compatibility}</div>
                    </div>
                    <div>
                      <div style={{fontSize:12, color:"#808090"}}>Sample Sentence</div>
                      <div style={{fontSize:13, color:"#B4B4C5", marginTop:4, fontStyle:"italic", background:"rgba(0,0,0,0.15)", padding:10, borderRadius:8, border:"1px solid var(--border)"}}>"{detailVoice.sentence}"</div>
                    </div>
                  </div>

                  <button onClick={()=>selectVoice(detailVoice.value)} className="gen-btn-huge" style={{height:44, fontSize:14.5, marginTop:"auto"}}>
                    Use This Voice
                  </button>
                </aside>
              )}

            </div>
          </div>
        </div>
      )}

      {/* ── PREMIUM STUDIO NAVBAR ── */}
      <nav className="nav">
        <div className="nav-brand" onClick={()=>onNavigate("home")}>
          <div className="nav-logo"><HiMicrophone size={16}/></div>
          <span className="nav-name">VoiceForge Studio</span>
        </div>
        <div className="nav-links">
          <button className="nav-link" onClick={()=>onNavigate("home")}>← Home</button>
          <button className="nav-link active">Studio</button>
          <button className="nav-link" onClick={()=>setSettingsOpen(true)}>Settings</button>
        </div>
        <div className="nav-end">
          <button className="nav-icon-btn nav-noti-btn" title="Notifications"><FiBell size={14}/></button>
          {status&&<div className="sbar-status"><span className="sbar-dot"/> <span className="sbar-text">{status}</span></div>}
          <span className="nav-kbd">Ctrl+Enter</span>
          {user && (
            <div className="nav-avatar-wrap">
              {user.picture && <img src={user.picture} alt={user.name} className="nav-avatar"/>}
              <button onClick={logout} className="nav-logout-btn" title="Sign out">Sign out</button>
            </div>
          )}
        </div>
      </nav>

      {/* ── TWO COLUMN MAIN STUDIO DASHBOARD ── */}
      <div className="studio-dashboard">
        
        {/* LEFT SIDEBAR */}
        <aside className="studio-sidebar">
          
          {/* History */}
          <div className="sidebar-group">
            <div className="credits-title" style={{display:"flex",alignItems:"center",gap:8}}><FiClock size={14}/> HISTORY</div>
            {history.length===0 ? (
              <div style={{fontSize:13,color:"var(--t3)",padding:"8px 12px"}}>No recent generations yet</div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:4}}>
                {history.map((h,i)=>(
                  <div key={i} className={`sidebar-hist-item${audioUrl===h.url?" active":""}`} onClick={()=>setAudioUrl(h.url)}>
                    <div className="hist-play"><HiPlay size={10}/></div>
                    <div style={{flex:1,minWidth:0}}>
                      <div className="hist-card-name" style={{fontSize:13,fontWeight:600}}>{h.name}</div>
                      <div className="hist-card-preview" style={{fontSize:11,color:"#B4B4C5", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{h.preview}</div>
                    </div>
                    <button onClick={(e)=>deleteHistoryItem(e, h.url)} style={{background:"transparent",border:"none",cursor:"pointer",color:"#808090",display:"flex",alignItems:"center",justifyContent:"center"}} title="Delete">
                      <RiDeleteBinLine size={13}/>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Templates */}
          <div className="sidebar-group">
            <div className="credits-title" style={{display:"flex",alignItems:"center",gap:8}}><FiFolder size={14}/> TEMPLATES</div>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:4}}>
              {TMPLS.map((t,i)=>(
                <button key={i} className="fc-l" onClick={()=>!loading&&handleTextChange(t.t)} disabled={loading} style={{width:"100%",borderRadius:12,padding:"10px 14px",background:"rgba(255,255,255,0.02)",border:"1px solid var(--border)",color:"var(--t2)",textAlign:"left", display:"flex", alignItems:"center", gap:8}}>
                  <span>{t.e}</span> {t.l}
                </button>
              ))}
            </div>
          </div>

          {/* Credits Panel */}
          <div className="sidebar-credits-panel">
            <div className="credits-title">Credits Usage</div>
            <div className="credits-bar-track">
              <div className="credits-bar-fill"/>
            </div>
            <div className="credits-meta">
              <span>7.5k / 10k words</span>
              <span>75%</span>
            </div>
          </div>

        </aside>

        {/* RIGHT WORKSPACE */}
        <main className="studio-workspace-right">
          
          <div className="studio-workspace" style={{padding:0,background:"none",border:"none",boxShadow:"none"}}>
            
            {/* 1. Header Toolbar Settings */}
            <div className="studio-toolbar" style={{marginBottom:32, display:"flex", alignItems:"center", gap:16, flexWrap:"wrap"}}>
              
              {/* Quick Voice Chip */}
              {mode === "preset" ? (
                <div className="quick-voice-chip" style={{
                  display:"flex", alignItems:"center", gap:14, background:"#111118", border:"1px solid var(--border)", 
                  padding:"6px 16px", borderRadius:16
                }}>
                  <div className={`vshow-av ${curV.gender==="female"?"vav-f":"vav-m"}`} style={{
                    width:28, height:28, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center",
                    background:curV.gender==="female"?"rgba(244, 63, 94, 0.1)":"rgba(56, 189, 248, 0.1)",
                    color:curV.gender==="female"?"var(--rose)":"var(--sky)", fontSize:12, fontWeight:700
                  }}>
                    {curV.gender==="female"?"♀":"♂"}
                  </div>
                  <div>
                    <span style={{fontSize:13, fontWeight:700, color:"#fff"}}>{curV.name}</span>
                    <span style={{fontSize:11.5, color:"#808090", marginLeft:8}}>({curV.lang})</span>
                  </div>
                  <button onClick={()=>setLibOpen(true)} className="fc-l" style={{
                    background:"rgba(139,92,246,0.15)", border:"1px solid rgba(139,92,246,0.3)", borderRadius:8, 
                    padding:"4px 10px", fontSize:11.5, fontWeight:600, color:"#fff", cursor:"pointer", marginLeft:8
                  }}>Change Voice</button>
                </div>
              ) : (
                <div className="quick-voice-chip" style={{
                  display:"flex", alignItems:"center", gap:14, background:"#111118", border:"1px solid var(--border)", 
                  padding:"6px 16px", borderRadius:16
                }}>
                  <div className="vshow-av vav-c" style={{
                    width:28, height:28, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center",
                    background:"rgba(139,92,246,0.1)", color:"#c084fc", fontSize:12, fontWeight:700
                  }}>
                    👤
                  </div>
                  <div>
                    <span style={{fontSize:13, fontWeight:700, color:"#fff"}}>
                      {selectedCloneId ? (savedClones.find(c=>c.id === selectedCloneId)?.name || "Cloned Voice") : (file ? file.name.slice(0,14) : "Custom Clone Sample")}
                    </span>
                  </div>
                  <button onClick={()=>fref.current?.click()} className="fc-l" style={{
                    background:"rgba(139,92,246,0.15)", border:"1px solid rgba(139,92,246,0.3)", borderRadius:8, 
                    padding:"4px 10px", fontSize:11.5, fontWeight:600, color:"#fff", cursor:"pointer", marginLeft:8
                  }}>Upload WAV</button>
                  <input ref={fref} type="file" accept=".wav,.mp3,.ogg,.flac" className="dz-fi" onChange={e=>validateFile(e.target.files[0])} disabled={loading} style={{display:"none"}}/>
                </div>
              )}

              {/* Big Browse Voice Library Button */}
              <button className="settings-tab-btn active" onClick={()=>setLibOpen(true)} style={{width:"auto", height:42, display:"flex", alignItems:"center", borderRadius:14, fontWeight:700, gap:6}}>
                🎙 Browse Voice Library
              </button>

              {/* Toggle Switch switcher */}
              <div className="mode-sw" style={{background:"#111118", border:"1px solid var(--border)", borderRadius:14, padding:4, display:"flex", gap:4}}>
                <button className={`settings-tab-btn ${mode==="preset"?"active":""}`} onClick={()=>{setMode("preset");setFile(null);}} disabled={loading} style={{width:"auto", padding:"8px 16px", borderRadius:10, display:"flex", alignItems:"center", gap:6, fontSize:13}}><HiPlay size={12}/>AI Voice Mode</button>
                <button className={`settings-tab-btn ${mode==="clone"?"active":""}`} onClick={()=>setMode("clone")} disabled={loading} style={{width:"auto", padding:"8px 16px", borderRadius:10, display:"flex", alignItems:"center", gap:6, fontSize:13}}><RiMic2Line size={12}/>Voice Clone Mode</button>
              </div>

              {/* Custom Language Dropdown */}
              <div className="custom-dropdown-container">
                <button className="custom-dropdown-trigger" onClick={()=>setLangOpen(!langOpen)}>
                  <span>{currentLangLabel}</span>
                  <FiChevronDown size={14}/>
                </button>
                {langOpen && (
                  <div className="custom-dropdown-menu">
                    {LANGS.map(([code, name])=>(
                      <div key={code} className={`custom-dropdown-item${lang===code?" selected":""}`} onClick={()=>{setLang(code);setLangOpen(false);}}>
                        <span>{name}</span>
                        {lang===code && <FiCheck size={12}/>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Auto detect button */}
              <button className="fc-l" onClick={detectLang} disabled={loading||!text.trim()} style={{padding:"0 16px", border:"1px solid var(--border)", borderRadius:12, height:42, fontSize:13, color:"#fff", cursor:"pointer"}}>Auto-detect Language</button>

            </div>

            {/* Clone Coming Soon Overlay Modal */}
            {mode === "clone" && (
              <div className="settings-overlay" onClick={()=>setMode("preset")}>
                <div className="settings-modal" style={{
                  maxWidth: 550,
                  height: "auto",
                  padding: 36,
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  border: "1.5px solid rgba(244, 63, 94, 0.3)",
                  boxShadow: "0 30px 70px rgba(0,0,0,0.9), 0 0 40px rgba(244, 63, 94, 0.15)",
                  borderRadius: 24,
                  background: "rgba(11, 11, 24, 0.98)"
                }} onClick={e=>e.stopPropagation()}>
                  <button onClick={()=>setMode("preset")} style={{
                    position: "absolute",
                    top: 20,
                    right: 20,
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    color: "#808090",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 4
                  }} title="Close"><HiX size={16}/></button>

                  <div style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: "rgba(244, 63, 94, 0.1)",
                    border: "1px solid rgba(244, 63, 94, 0.35)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--rose)",
                    marginBottom: 20,
                    fontSize: 24,
                    fontWeight: 700
                  }}>⚠️</div>
                  
                  <h3 style={{fontSize: 22, fontWeight: 800, color: "#fff", marginBottom: 12}}>Feature Coming Soon</h3>
                  
                  <p style={{fontSize: 14.5, color: "#B4B4C5", maxWidth: 440, lineHeight: 1.6, marginBottom: 24}}>
                    Custom neural voice cloning is currently under active development. Uploading raw audio files, speech fine-tuning, and premium multilingual voice options will be available in the next release!
                  </p>
                  
                  <button className="settings-tab-btn active" onClick={()=>setMode("preset")} style={{width: "auto", padding: "10px 24px", borderRadius: 12, fontWeight: 700, background: "rgba(244, 63, 94, 0.15)", borderColor: "rgba(244, 63, 94, 0.3)", color: "var(--rose)", cursor: "pointer"}}>
                    Back to AI Voice Mode
                  </button>
                </div>
              </div>
            )}
            {ferr && <div className="dz-err" style={{color:"var(--rose)", fontSize:13, marginBottom:20}}>⚠ {ferr}</div>}

            {/* 2. Text Editor Wrap */}
            <div className="board-editor-wrap">
              <div className="board-editor-header">
                <div style={{display:"flex", alignItems:"center", gap:8}}>
                  <span className="autosave-dot"/>
                  <span style={{fontSize:13, fontWeight:600, color:"#B4B4C5"}}>VoiceForge AI Editor</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <button onClick={handleUndo} className="fc-l" style={{padding:"6px 12px", border:"1px solid var(--border)", borderRadius:10, display:"flex", alignItems:"center", gap:6, fontSize:12}} disabled={historyIndex === 0} title="Undo"><FiRotateCcw size={12}/> <span className="btn-label-text">Undo</span></button>
                  <button onClick={handleRedo} className="fc-l" style={{padding:"6px 12px", border:"1px solid var(--border)", borderRadius:10, display:"flex", alignItems:"center", gap:6, fontSize:12}} disabled={historyIndex === textHistory.length - 1} title="Redo"><FiRotateCw size={12}/> <span className="btn-label-text">Redo</span></button>
                  <button onClick={pasteText} className="fc-l" style={{padding:"6px 12px", border:"1px solid var(--border)", borderRadius:10, display:"flex", alignItems:"center", gap:6, fontSize:12}} disabled={loading} title="Paste"><FiClipboard size={12}/> <span className="btn-label-text">Paste</span></button>
                  <button onClick={clearText} className="fc-l" style={{padding:"6px 12px", border:"1px solid var(--border)", borderRadius:10, display:"flex", alignItems:"center", gap:6, fontSize:12}} disabled={loading} title="Clear"><FiTrash2 size={12}/> <span className="btn-label-text">Clear</span></button>
                  <button onClick={importText} className="fc-l" style={{padding:"6px 12px", border:"1px solid var(--border)", borderRadius:10, display:"flex", alignItems:"center", gap:6, fontSize:12}} disabled={loading} title="Import TXT"><FiUpload size={12}/> <span className="btn-label-text">Import</span></button>
                  <button onClick={exportText} className="fc-l" style={{padding:"6px 12px", border:"1px solid var(--border)", borderRadius:10, display:"flex", alignItems:"center", gap:6, fontSize:12}} disabled={!text.trim()} title="Export TXT"><FiDownload size={12}/> <span className="btn-label-text">Export</span></button>
                  <button onClick={copyText} className="fc-l" style={{padding:"6px 12px", border:"1px solid var(--border)", borderRadius:10, display:"flex", alignItems:"center", gap:6, fontSize:12}} disabled={!text.trim()} title="Copy">
                    <FiCopy size={12}/> <span className="btn-label-text">{copied?"Copied":"Copy"}</span>
                  </button>
                </div>
              </div>

              <div className="board-editor-inner">
                {/* Line numbers column */}
                <div className="board-line-numbers">
                  <div>1</div>
                  <div>2</div>
                  <div>3</div>
                  <div>4</div>
                  <div>5</div>
                </div>
                
                <textarea className="board-textarea" value={text} onChange={e=>handleTextChange(e.target.value)} disabled={loading}
                  placeholder="Welcome to the future of AI voice technology. Type or paste your script here..." spellCheck/>
              </div>

              <div className="board-editor-footer">
                <div className="board-meta" style={{display:"flex", gap:12, alignItems:"center"}}>
                  <span className="board-meta-chip" style={{fontSize:12, color:"#B4B4C5", background:"rgba(255,255,255,0.02)", padding:"4px 10px", borderRadius:6}}>{wc} words</span>
                  <span className="board-meta-chip" style={{fontSize:12, color:"#B4B4C5", background:"rgba(255,255,255,0.02)", padding:"4px 10px", borderRadius:6}}>{text.length} characters</span>
                  {estSec>0&&<span className="board-meta-chip" style={{fontSize:12, color:"#B4B4C5", background:"rgba(255,255,255,0.02)", padding:"4px 10px", borderRadius:6}}>~{estSec}s est. duration</span>}
                  {text.trim() && (
                    <span className="board-meta-chip animate-fade-in" style={{fontSize:12, color:"#a855f7", background:"rgba(168,85,247,0.08)", border:"1px solid rgba(168,85,247,0.18)", padding:"4px 10px", borderRadius:6, display:"flex", alignItems:"center", gap:4, fontWeight:600}}>
                      🔍 Detected Language: {getFlag(detectedLang)}
                    </span>
                  )}
                </div>
                <span className={`board-cnt${text.length>MAX*.85?" w":""}${text.length>=MAX?" o":""}`} style={{fontSize:13, fontWeight:600}}>{text.length.toLocaleString()} / {MAX.toLocaleString()}</span>
              </div>
            </div>

            {/* Voice compatibility warning message */}
            {mode === "preset" && text.trim() && curV.langCode !== detectedLang && (
              <div className="compatibility-warning-banner animate-fade-in" style={{
                background: "rgba(239, 68, 68, 0.08)",
                border: "1px solid rgba(239, 68, 68, 0.18)",
                borderRadius: 16,
                padding: "12px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                marginTop: 14,
                marginBottom: 14
              }}>
                <span style={{fontSize: 13, color: "#f87171", display: "flex", alignItems: "center", gap: 6, fontWeight: 500}}>
                  ⚠️ Selected voice ({curV.name}) is optimized for {curV.lang.split(" ")[0]} but text is in {getFlag(detectedLang).split(" ")[0]}.
                </span>
                <button onClick={autoSwitchVoice} style={{
                  background: "rgba(239, 68, 68, 0.15)",
                  border: "1px solid rgba(239, 68, 68, 0.3)",
                  color: "#f87171",
                  borderRadius: 10,
                  padding: "6px 14px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}>
                  Switch to compatible voice
                </button>
              </div>
            )}

            {/* 3. Advanced Parameters Slider Grid */}
            <div className="parameters-grid">
              
              {/* Voice Speed */}
              <div className="parameter-slider-group">
                <div className="slider-label-row">
                  <span>Speed Multiplier</span>
                  <span className="slider-value">{speed}x</span>
                </div>
                <input type="range" className="custom-range-slider" min="0.5" max="2.0" step="0.05" value={speed} onChange={e=>setSpeed(parseFloat(e.target.value))} disabled={loading}/>
              </div>

              {/* Pitch */}
              <div className="parameter-slider-group">
                <div className="slider-label-row">
                  <span>Pitch Shifts</span>
                  <span className="slider-value">{pitch > 0 ? `+${pitch}` : pitch} semitones</span>
                </div>
                <input type="range" className="custom-range-slider" min="-3" max="3" step="0.5" value={pitch} onChange={e=>setPitch(parseFloat(e.target.value))} disabled={loading}/>
              </div>

              {/* Stability Slider */}
              <div className="parameter-slider-group">
                <div className="slider-label-row">
                  <span>Stability</span>
                  <span className="slider-value">{stability}%</span>
                </div>
                <input type="range" className="custom-range-slider" min="0" max="100" step="1" value={stability} onChange={e=>setStability(parseInt(e.target.value))} disabled={loading}/>
              </div>

              {/* Similarity Slider */}
              <div className="parameter-slider-group">
                <div className="slider-label-row">
                  <span>Similarity Match</span>
                  <span className="slider-value">{similarity}%</span>
                </div>
                <input type="range" className="custom-range-slider" min="0" max="100" step="1" value={similarity} onChange={e=>setSimilarity(parseInt(e.target.value))} disabled={loading}/>
              </div>

              {/* Custom Accent Dropdown */}
              <div className="parameter-slider-group" style={{position:"relative"}}>
                <div className="slider-label-row">
                  <span>Accent Accentuation</span>
                </div>
                <div className="custom-dropdown-container" style={{width:"100%"}}>
                  <button className="custom-dropdown-trigger" onClick={()=>setAccentOpen(!accentOpen)}>
                    <span>{accent}</span>
                    <FiChevronDown size={14}/>
                  </button>
                  {accentOpen && (
                    <div className="custom-dropdown-menu">
                      {["Neutral Accent", "British Accent", "American Accent", "Australian Accent"].map(acc=>(
                        <div key={acc} className={`custom-dropdown-item${accent===acc?" selected":""}`} onClick={()=>{setAccent(acc);setAccentOpen(false);}}>
                          <span>{acc}</span>
                          {accent===acc && <FiCheck size={12}/>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Custom Emotion Dropdown */}
              <div className="parameter-slider-group" style={{position:"relative"}}>
                <div className="slider-label-row">
                  <span>Emotion Expression</span>
                </div>
                <div className="custom-dropdown-container" style={{width:"100%"}}>
                  <button className="custom-dropdown-trigger" onClick={()=>setEmotionOpen(!emotionOpen)}>
                    <span>{emotion}</span>
                    <FiChevronDown size={14}/>
                  </button>
                  {emotionOpen && (
                    <div className="custom-dropdown-menu">
                      {["Natural", "Excited", "Calm", "Professional"].map(em=>(
                        <div key={em} className={`custom-dropdown-item${emotion===em?" selected":""}`} onClick={()=>{setEmotion(em);setEmotionOpen(false);}}>
                          <span>{em}</span>
                          {emotion===em && <FiCheck size={12}/>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Pause Control */}
              <div className="parameter-slider-group">
                <div className="slider-label-row">
                  <span>Pause Control (Breathing)</span>
                  <span className="slider-value">{pauseControl}%</span>
                </div>
                <input type="range" className="custom-range-slider" min="0" max="100" step="1" value={pauseControl} onChange={e=>setPauseControl(parseInt(e.target.value))} disabled={loading}/>
              </div>

              {/* Energy */}
              <div className="parameter-slider-group">
                <div className="slider-label-row">
                  <span>Energy / Volume</span>
                  <span className="slider-value">{energy}%</span>
                </div>
                <input type="range" className="custom-range-slider" min="0" max="100" step="1" value={energy} onChange={e=>setEnergy(parseInt(e.target.value))} disabled={loading}/>
              </div>

              {/* Clarity */}
              <div className="parameter-slider-group">
                <div className="slider-label-row">
                  <span>Clarity / Sharpness</span>
                  <span className="slider-value">{clarity}%</span>
                </div>
                <input type="range" className="custom-range-slider" min="0" max="100" step="1" value={clarity} onChange={e=>setClarity(parseInt(e.target.value))} disabled={loading}/>
              </div>

              {/* Voice Warmth */}
              <div className="parameter-slider-group">
                <div className="slider-label-row">
                  <span>Voice Warmth</span>
                  <span className="slider-value">{warmth}%</span>
                </div>
                <input type="range" className="custom-range-slider" min="0" max="100" step="1" value={warmth} onChange={e=>setWarmth(parseInt(e.target.value))} disabled={loading}/>
              </div>

              {/* Toggle Switch instead of Checkbox */}
              <div className="parameter-slider-group" style={{gridColumn:"1 / -1", marginTop:12}}>
                <div className="custom-checkbox-row" onClick={()=>!loading && setBoost(!boost)}>
                  <div className={`custom-checkbox-box ${boost?"checked":""}`}>
                    <FiCheck size={12}/>
                  </div>
                  <span style={{fontWeight:600}}>Enable Speaker Boost (+10dB SNR audio synthesis mode)</span>
                </div>
              </div>

            </div>

            {/* 4. Action Synthesize Area */}
            <div style={{marginTop:36}}>
              <button className="gen-btn-huge" onClick={generate} disabled={disabled||loading}>
                {loading ? (
                  <div style={{display:"flex", alignItems:"center", gap:8}}>
                    <span className="gen-spin" style={{borderColor:"#fff", borderTopColor:"transparent"}}/>
                    <span>{status || "Synthesizing Speech..."}</span>
                  </div>
                ) : (
                  <span>Synthesize High-fidelity Audio</span>
                )}
              </button>
            </div>

            {/* Redesigned Generated WAV Audio Section */}
            {audioUrl && (
              <div id="generated-audio-section" className="advanced-player">
                {/* Audio Result Success Banner & Metadata Details */}
                <div className="ap-header-wrap">
                  <div className="ap-header-top">
                    <div className="ap-status-banner">
                      <span className="ap-status-text">✅ Audio Generated Successfully</span>
                    </div>
                    
                    <div className="ap-actions">
                      <button onClick={saveToHistory} className="fc-l ap-action-btn">
                        <FiStar size={12}/> {saveSuccess ? "Saved" : "Save to History"}
                      </button>
                      <a href={audioUrl} download="synthesized_speech.wav" className="fc-l ap-action-btn">
                        <FiDownload size={12}/> Download WAV
                      </a>
                      <button onClick={()=>triggerToast("🔗 Share link copied to clipboard!")} className="fc-l ap-action-btn">
                        <FiShare2 size={12}/> Share
                      </button>
                      <button onClick={()=>{setAudioUrl(null); setGenMetadata(null);}} className="fc-l ap-action-btn btn-rose">
                        <RiDeleteBinLine size={12}/> Delete
                      </button>
                    </div>
                  </div>

                  {genMetadata && (
                    <div className="ap-meta-grid">
                      <div className="ap-meta-item">
                        <div className="ap-meta-label">Voice Model</div>
                        <div className="ap-meta-value">{genMetadata.voiceName}</div>
                      </div>
                      <div className="ap-meta-item">
                        <div className="ap-meta-label">Language</div>
                        <div className="ap-meta-value">{genMetadata.language}</div>
                      </div>
                      <div className="ap-meta-item">
                        <div className="ap-meta-label">Duration</div>
                        <div className="ap-meta-value">{genMetadata.duration}s</div>
                      </div>
                      <div className="ap-meta-item">
                        <div className="ap-meta-label">Length</div>
                        <div className="ap-meta-value">{genMetadata.characters} chars</div>
                      </div>
                      <div className="ap-meta-item">
                        <div className="ap-meta-label">File Size</div>
                        <div className="ap-meta-value">{genMetadata.fileSize}</div>
                      </div>
                      <div className="ap-meta-item">
                        <div className="ap-meta-label">Format</div>
                        <div className="ap-meta-value">{genMetadata.format} · {genMetadata.sampleRate}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Custom Audio Element & Controls */}
                <audio ref={audioRef} src={audioUrl} onTimeUpdate={handleAudioTimeUpdate} onLoadedMetadata={handleAudioLoadedMetadata} onEnded={handleAudioEnded}/>

                <div className="ap-body">
                  {/* Waveform Visualization & Slider Track */}
                  <div className="ap-wave-wrap">
                    {/* SVG wave animation */}
                    <div className="ap-waveform-anim">
                      {[...Array(60)].map((_, i) => {
                        const hVal = audioPlaying ? Math.sin(i * 0.3 + audioCurrentTime * 8) * 15 + 20 : 12;
                        return (
                          <div key={i} className="ap-wave-bar" style={{
                            height:`${Math.max(4, hVal)}px`,
                            background: (i / 60) * audioDuration <= audioCurrentTime ? "linear-gradient(to top, #8B5CF6, #A855F7)" : "rgba(255,255,255,0.06)",
                          }}/>
                        );
                      })}
                    </div>
                    {/* Clickable Progress Slider */}
                    <div className="ap-progress-row">
                      <span className="ap-time-label">{formatTime(audioCurrentTime)}</span>
                      <input type="range" min="0" max={audioDuration || 100} value={audioCurrentTime} onChange={e=>seekAudio(parseFloat(e.target.value))} className="ap-progress-range"/>
                      <span className="ap-time-label">{formatTime(audioDuration)}</span>
                    </div>
                  </div>

                  {/* Player Controls Bar */}
                  <div className="ap-controls-bar">
                    <div className="ap-playback-btns">
                      <button onClick={restartAudio} className="fc-l ap-icon-btn" title="Restart"><FiSkipBack size={14}/></button>
                      <button onClick={()=>skipAudio(-5)} className="fc-l ap-icon-btn" title="Rewind 5s">-5s</button>
                      <button onClick={togglePlayPause} className="ap-play-pause-btn">
                        {audioPlaying ? <FiPause size={18}/> : <FiPlay size={18}/>}
                      </button>
                      <button onClick={()=>skipAudio(5)} className="fc-l ap-icon-btn" title="Forward 5s">+5s</button>
                      <button onClick={toggleLoop} className={`fc-l ap-icon-btn${audioLoop ? " active" : ""}`} title="Loop Toggle"><FiRepeat size={14}/></button>
                    </div>

                    {/* Speed & Volume Section */}
                    <div className="ap-settings-btns">
                      {/* Playback speed dropdown */}
                      <div className="ap-speed-selector">
                        <span className="ap-setting-label">Speed</span>
                        <select value={audioPlaybackRate} onChange={e=>changePlaybackRate(parseFloat(e.target.value))} className="ap-select">
                          <option value="0.5">0.5x</option>
                          <option value="1.0">1.0x</option>
                          <option value="1.25">1.25x</option>
                          <option value="1.5">1.5x</option>
                          <option value="2.0">2.0x</option>
                        </select>
                      </div>

                      {/* Volume controls */}
                      <div className="ap-volume-control">
                        <button onClick={toggleMute} className="ap-mute-btn">
                          {audioMuted || audioVolume === 0 ? <FiVolumeX size={16}/> : audioVolume < 0.5 ? <FiVolume1 size={16}/> : <FiVolumeHigh size={16}/>}
                        </button>
                        <input type="range" min="0" max="1" step="0.05" value={audioMuted ? 0 : audioVolume} onChange={e=>changeVolume(parseFloat(e.target.value))} className="ap-volume-range"/>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>

        </main>
      </div>

      {/* ── STUDIO FOOTER ── */}
      <footer className="studio-footer">
        <div className="studio-footer-in">
          <div style={{display:"flex", alignItems:"center", gap:8}}>
            <span style={{color:"#fff", fontWeight:700}}>VoiceForge Studio</span>
            <span>·</span>
            <span>v1.2.0</span>
            <span>·</span>
            <span style={{color:"var(--grn)"}}>● System Online</span>
          </div>
          <div style={{display:"flex", gap:20, flexWrap:"wrap", justifyContent:"center"}}>
            <span>Privacy Policy</span>
            <span>Terms of Service</span>
            <span>GitHub Documentation</span>
            <span>Support</span>
          </div>
          <div>
            <span>© 2024 VoiceForge AI · Developed by <a href="https://github.com/fahadalimazari" target="_blank" rel="noreferrer" style={{color:"#8B5CF6", textDecoration:"none", fontWeight:"600"}}>Fahad Ali Mazari</a></span>
          </div>
        </div>
      </footer>

    </div>
  );
}
